import { getGuid } from 'owa-guid';
import type { WindowKey } from 'owa-bizchat-types';
import { getBizChatWindowKey } from './getBizChatWindowKey';

interface SidepaneSessionData {
    sidePaneOpenToCloseSessionId: string;
    openedTimestamp: number;
}

const sessionByWindow: Map<WindowKey, SidepaneSessionData> = new Map();

/**
 * Gets or creates a sidePaneOpenToCloseSessionId for the given target window.
 * Creates a new session ID and records the open timestamp if one doesn't exist.
 */
export function getOrCreateSidePaneOpenToCloseSessionId(targetWindow?: Window): string {
    const windowKey = getBizChatWindowKey(targetWindow);
    let sessionData = sessionByWindow.get(windowKey);

    if (!sessionData) {
        sessionData = {
            sidePaneOpenToCloseSessionId: getGuid(),
            openedTimestamp: Date.now(),
        };
        sessionByWindow.set(windowKey, sessionData);
    }

    return sessionData.sidePaneOpenToCloseSessionId;
}

/**
 * Gets the existing sidePaneOpenToCloseSessionId for the given target window without creating a new one.
 * Returns undefined if no session exists.
 */
export function getSidePaneOpenToCloseSessionId(targetWindow: Window): string | undefined {
    const windowKey = getBizChatWindowKey(targetWindow);
    return sessionByWindow.get(windowKey)?.sidePaneOpenToCloseSessionId;
}

/**
 * Gets the time in milliseconds since the sidepane was opened for the given target window.
 * Returns undefined if no session exists.
 */
export function getTimeSinceSidepaneOpened(targetWindow: Window): number | undefined {
    const windowKey = getBizChatWindowKey(targetWindow);
    const sessionData = sessionByWindow.get(windowKey);

    if (!sessionData) {
        return undefined;
    }

    return Date.now() - sessionData.openedTimestamp;
}

/**
 * Clears the sidePaneOpenToCloseSessionId for the given target window.
 * Called when the sidepane is closed.
 */
export function clearSidePaneOpenToCloseSessionId(targetWindow: Window): void {
    const windowKey = getBizChatWindowKey(targetWindow);
    sessionByWindow.delete(windowKey);
}

/**
 * Returns a provider closure that yields the BizChat side pane correlation fields
 * (`sidePaneOpenToCloseSessionId`, `timeSinceOpened`) for the given window each time
 * it is invoked.
 *
 * Intended for non-entrypoint scenarios that want to correlate their telemetry with the
 * current BizChat side pane session — e.g., Coaching and Highlight & Rewrite — per the
 * "Adding New BizChat Entrypoints" wiki guidance. Both fields are `undefined` when the
 * pane is not currently open.
 *
 * Use this with `CopilotTelemetrySession`'s `bizChatCorrelationProvider` constructor
 * parameter; the indirection exists to avoid a circular dependency between
 * `owa-copilot-telemetry` and `owa-copilot-chat-drawer-store`.
 */
export function createBizChatCorrelationProvider(targetWindow: Window): () => {
    sidePaneOpenToCloseSessionId: string | undefined;
    timeSinceOpened: number | undefined;
} {
    return () => ({
        sidePaneOpenToCloseSessionId: getSidePaneOpenToCloseSessionId(targetWindow),
        timeSinceOpened: getTimeSinceSidepaneOpened(targetWindow),
    });
}
